import { TaskColumn } from "@common/types/views/tasksView";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    type DataGridListTriggerContent,
    ListEditTrigger,
} from "@design-system/DataGrid";
import { useEntities } from "@hooks/useEntities";
import { TaskAssigneeLabel } from "./cells/TaskAssigneeLabel";
import { TaskEntityAvatars } from "./cells/TaskEntityAvatars";
import { TaskAssigneesSummary } from "./cells/taskGroupSummaries";
import type { TaskListTaskRow } from "./cells/taskRowCells";
import {
    col,
    commitTask,
    NO_VALUE,
    type TaskColumnBuild,
    toTaskCell,
} from "./editableTaskColumns";
import { useAssigneeOptions } from "./taskPickerOptions";

const AssigneeValue = ({ task }: { task: TaskListTaskRow }) =>
    task.assigneeId ? (
        <TaskAssigneeLabel task={task} assigneeId={task.assigneeId} />
    ) : null;

const TaskEntitiesByIds = ({ ids }: { ids: readonly number[] }) => {
    const entities = useEntities().filter((entity) => ids.includes(entity.id));
    return <TaskEntityAvatars entities={entities} />;
};

const EntitiesValue = ({ task }: { task: TaskListTaskRow }) =>
    task.entityIds?.length ? <TaskEntitiesByIds ids={task.entityIds} /> : null;

const toEntityNames = (entities: ReadonlyArray<{ name: string | null }>) =>
    entities.flatMap((entity) => (entity.name ? [entity.name] : []));

/**
 * The Entities editor: the entity combobox the cell opens, checking and
 * unchecking saves each change. Its trigger is named after the entities.
 */
const TaskEntitiesPicker = ({
    value,
    columnLabel,
    isOpen,
    onOpenChange,
    trigger,
    save,
}: {
    value: readonly number[];
    columnLabel: string;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    trigger: DataGridListTriggerContent;
    save: (entityIds: readonly number[]) => void;
}) => (
    <EntityFilterCombobox
        mode="selection"
        filters={{ entityIds: [...value] }}
        onChange={({ entityIds }) => save(entityIds ?? [])}
        popupLabel={columnLabel}
        open={isOpen}
        onOpenChange={onOpenChange}
        renderTrigger={({ triggerProps, selectedEntities }) => (
            <ListEditTrigger
                {...triggerProps}
                {...trigger}
                display={<TaskEntityAvatars entities={selectedEntities} />}
                aria-label={
                    toEntityNames(selectedEntities).join(", ") ||
                    (trigger["aria-label"] ?? trigger.placeholder)
                }
            />
        )}
    />
);

/** Assignee: the row's workspace members, loaded once the picker opens. */
export const buildAssigneeColumn = ({ options, actions }: TaskColumnBuild) =>
    col.text(
        { id: TaskColumn.ASSIGNEE, value: NO_VALUE },
        {
            ...options,
            render: toTaskCell(AssigneeValue),
            summary: TaskAssigneesSummary,
            edit: col.edit.picker({
                useOptions: useAssigneeOptions,
                emptyOption: { label: "Unassigned" },
                value: (row) => row.assigneeId ?? null,
                commit: commitTask({
                    actions,
                    toBody: (assigneeId) => ({ assigneeId }),
                }),
                placeholder: "Assign to",
            }),
        },
    );

/** Entities: avatars, edited in the entity combobox. */
export const buildEntitiesColumn = ({ options, actions }: TaskColumnBuild) =>
    col.custom({
        ...options,
        id: TaskColumn.ENTITIES,
        render: toTaskCell(EntitiesValue),
        edit: col.edit.custom<readonly number[]>({
            popup: "listbox",
            value: (row) => row.entityIds ?? [],
            commit: commitTask({
                actions,
                toBody: (entityIds) => ({ entityIds: [...entityIds] }),
            }),
            placeholder: "Add entities",
            layout: "fit",
            render: (props) => <TaskEntitiesPicker {...props} />,
        }),
    });
